import {
  differenceInCalendarDays,
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  format,
  isToday,
  isYesterday,
} from "date-fns";

const DEFAULT_LOCALE = "pt-BR";

/**
 * Converte `valor` pra número, caindo em `fallback` quando o resultado não é
 * finito (undefined/null/string não numérica → NaN, ou Infinity/-Infinity).
 * Diferente de `Number(valor) ?? fallback`: `??` só cai no fallback quando o
 * valor é null/undefined, nunca quando é NaN — então um campo ausente no
 * payload (Number(undefined) === NaN) passa direto e vira "NaN" na tela.
 */
export const numeroOuPadrao = (valor, fallback = 0) => {
  const n = Number(valor);
  return Number.isFinite(n) ? n : fallback;
};

export const formatNumber = (value, locale = DEFAULT_LOCALE) => {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  return new Intl.NumberFormat(locale).format(value);
};

export const formatPercent = (value, { decimals = 0 } = {}) => {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  return `${Number(value).toLocaleString(DEFAULT_LOCALE, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}%`;
};

export const formatDelta = (value, { suffix = "pp", signed = true } = {}) => {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  const sign = signed && value > 0 ? "+" : "";
  return `${sign}${value}${suffix}`;
};

export const formatNps = (value) => {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  return Math.round(Number(value)).toString();
};

export const formatShortDate = (isoOrDate) => {
  if (!isoOrDate) return "—";
  const d = typeof isoOrDate === "string" ? new Date(isoOrDate) : isoOrDate;
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(DEFAULT_LOCALE, {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  });
};

// "agora", "há 5 min", "há 3h", "há 2 dias" e, a partir de 7 dias, "27/10/2026".
export const formatRelativeTime = (isoOrDate) => {
  if (!isoOrDate) return "";
  const d = typeof isoOrDate === "string" ? new Date(isoOrDate) : isoOrDate;
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const minutes = differenceInMinutes(now, d);
  if (minutes < 1) return "agora";
  if (minutes < 60) return `há ${minutes} min`;
  const hours = differenceInHours(now, d);
  if (hours < 24) return `há ${hours}h`;
  const days = differenceInDays(now, d);
  if (days < 7) return `há ${days} dia${days === 1 ? "" : "s"}`;
  return format(d, "dd/MM/yyyy");
};

// "24/10/2026 às 14:32"
export const formatFullDateTime = (isoOrDate) => {
  if (!isoOrDate) return "";
  const d = typeof isoOrDate === "string" ? new Date(isoOrDate) : isoOrDate;
  if (Number.isNaN(d.getTime())) return "";
  return format(d, "dd/MM/yyyy 'às' HH:mm");
};

// Grupo de data da central de notificações: Hoje, Ontem, Últimos 7 dias ou Mais antigas.
export const notificationDateGroup = (isoOrDate) => {
  const d = typeof isoOrDate === "string" ? new Date(isoOrDate) : isoOrDate;
  if (isToday(d)) return "Hoje";
  if (isYesterday(d)) return "Ontem";
  if (differenceInCalendarDays(new Date(), d) < 7) return "Últimos 7 dias";
  return "Mais antigas";
};

export const truncate = (text, max = 120) => {
  if (!text) return "";
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
};

export const formatPhoneNumber = (value) => {
  const numbers = value.replace(/\D/g, "");

  if (numbers.length <= 2) {
    return `(${numbers}`;
  }

  if (numbers.length <= 7) {
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
  }

  if (numbers.length <= 11) {
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(
      7,
      11
    )}`;
  }

  return value;
};

export const isValidPhone = (value) => {
  const numbers = value.replace(/\D/g, "");
  return !/^(\d)\1+$/.test(numbers);
};
